import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useUserProfile } from './UserProfileContext';

export type ViewMode = 'explorer' | 'recruiter';

const STORAGE_KEY = 'cevee_view_mode';

const readStored = (): ViewMode | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'explorer' || v === 'recruiter' ? v : null;
  } catch {
    return null;
  }
};

const writeStored = (mode: ViewMode) => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* ignore (private mode / disabled storage) */
  }
};

interface ViewModeContextType {
  /** The audience experience currently shown in the shell (nav/tabs/landing). */
  viewMode: ViewMode;
  /** Persist + switch the shell experience (marks the choice as explicit). */
  setViewMode: (mode: ViewMode) => void;
  /** Convenience flag for the recruiter shell. */
  isViewingAsRecruiter: boolean;
  /**
   * The role tied to the account (is_recruiter). null while logged out / profile
   * not yet loaded. Drives feature gates + the default view mode.
   */
  accountMode: ViewMode | null;
  /** True when the shown experience differs from the account's natural one. */
  isMismatch: boolean;
}

const ViewModeContext = createContext<ViewModeContextType | undefined>(undefined);

export const ViewModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userProfile } = useUserProfile();

  // Read synchronously to avoid a flicker on first paint.
  const [viewMode, setViewModeState] = useState<ViewMode>(() => readStored() ?? 'explorer');
  // Whether the user has explicitly chosen a mode. Until then the shell follows
  // the account role automatically (so a fresh recruiter login lands recruiter-side).
  const [hasExplicit, setHasExplicit] = useState<boolean>(() => readStored() !== null);

  const setViewMode = useCallback((mode: ViewMode) => {
    writeStored(mode);
    setViewModeState(mode);
    setHasExplicit(true);
  }, []);

  const accountMode: ViewMode | null = userProfile
    ? (userProfile.is_recruiter ? 'recruiter' : 'explorer')
    : null;

  // Follow the account role until the user explicitly toggles.
  useEffect(() => {
    if (hasExplicit) return;
    if (accountMode) setViewModeState(accountMode);
  }, [accountMode, hasExplicit]);

  const value: ViewModeContextType = {
    viewMode,
    setViewMode,
    isViewingAsRecruiter: viewMode === 'recruiter',
    accountMode,
    isMismatch: accountMode !== null && accountMode !== viewMode,
  };

  return <ViewModeContext.Provider value={value}>{children}</ViewModeContext.Provider>;
};

export const useViewMode = () => {
  const context = useContext(ViewModeContext);
  if (context === undefined) {
    throw new Error('useViewMode must be used within a ViewModeProvider');
  }
  return context;
};
